/**
 * Session Timeout Manager - Handles session timeout due to inactivity using idle-timeout library
 */
import * as bootstrap from 'bootstrap';
import idleTimeout from 'idle-timeout';
import { showNotification } from '@/helpers';
export class SessionTimeoutManager {
    constructor() {
        this.IDLE_TIME = 15 * 60 * 1000; // 15 minutos de inactividad (en milisegundos)
        this.WARNING_TIME = 20; // 20 segundos para responder
        this.idleTimeoutInstance = null;
        this.countdownInterval = null;
        this.modal = null;
        this.handleModalHidden = null;
    }
    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeIdleTimer();
            });
        }
        else {
            this.initializeIdleTimer();
        }
    }
    initializeIdleTimer() {
        // Verificar si el usuario está autenticado buscando el sidebar que solo existe en páginas privadas
        const sidebarMenu = document.getElementById('sidebarMenu');
        if (!sidebarMenu) {
            // Usuario no autenticado, no inicializar el timeout
            return;
        }
        // Initialize idle-timeout
        this.idleTimeoutInstance = idleTimeout((element, timeout) => {
            // Callback when timeout is reached
            this.handleIdleTimeout();
        }, {
            timeout: this.IDLE_TIME,
            loop: false, // Don't restart automatically
            element: document.documentElement
        });
    }
    handleIdleTimeout() {
        // Destroy the idle timeout instance
        if (this.idleTimeoutInstance) {
            this.idleTimeoutInstance.destroy();
            this.idleTimeoutInstance = null;
        }
        // Show warning dialog
        this.showWarningDialog();
    }
    showWarningDialog() {
        // Crear el modal si no existe
        this.createWarningModal();
        // Mostrar el modal
        this.modal.show();
        // Iniciar cuenta regresiva
        this.startCountdown();
    }
    createWarningModal() {
        if (this.modal) {
            return; // Ya existe
        }
        const idleTimeMinutes = this.IDLE_TIME / (60 * 1000);
        const modalHtml = `
            <div class="modal fade" id="sessionTimeoutModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header bg-warning text-dark">
                            <h5 class="modal-title">
                                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                                Sesión por Inactividad
                            </h5>
                        </div>
                        <div class="modal-body">
                            <p class="mb-3">
                                <strong>Ha estado inactivo por ${idleTimeMinutes} minuto(s).</strong>
                            </p>
                            <p class="mb-4">
                                ¿Desea continuar trabajando?
                            </p>

                            <div class="text-center bg-body-secondary p-4 rounded border">
                                <div class="mb-2">
                                    <i class="bi bi-clock-history text-warning timeout-icon"></i>
                                </div>
                                <h6 class="mb-3">Tiempo restante para cerrar sesión</h6>
                                <div class="display-1 fw-bold text-primary mb-2" id="countdown">${this.WARNING_TIME}</div>
                                <div class="text-muted">segundos</div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                                <i class="bi bi-x-circle me-1"></i> No
                            </button>
                            <button type="button" class="btn btn-primary" id="continueSession">
                                <i class="bi bi-check-circle me-1"></i> Sí, continuar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        // Agregar el modal al body
        document.body.insertAdjacentHTML('beforeend', modalHtml);
        // Crear instancia del modal
        const modalElement = document.getElementById('sessionTimeoutModal');
        this.modal = new bootstrap.Modal(modalElement);
        // Configurar eventos de los botones
        const continueButton = document.getElementById('continueSession');
        if (continueButton) {
            continueButton.addEventListener('click', () => {
                this.continueSession();
            });
        }
        // Cuando se cierra el modal (botón No o clic fuera)
        modalElement.addEventListener('hidden.bs.modal', this.handleModalHidden = () => {
            this.handleTimeoutExpired('logout');
        });
    }
    startCountdown() {
        let secondsLeft = this.WARNING_TIME;
        // Actualizar el contador inicialmente
        const countdownElement = document.getElementById('countdown');
        if (countdownElement) {
            countdownElement.textContent = secondsLeft.toString();
        }
        // Iniciar cuenta regresiva
        this.countdownInterval = setInterval(() => {
            secondsLeft--;
            const countdownElement = document.getElementById('countdown');
            const countdownTextElement = document.getElementById('countdown-text');
            if (countdownElement) {
                countdownElement.textContent = secondsLeft.toString();
            }
            if (secondsLeft <= 0) {
                // Tiempo expirado
                this.stopCountdown();
                this.handleTimeoutExpired('force-logout');
            }
        }, 1000);
    }
    stopCountdown() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    }
    continueSession() {
        // Detener cuenta regresiva
        this.stopCountdown();
        // Cerrar modal SIN disparar el evento hidden.bs.modal
        if (this.modal) {
            const modalElement = document.getElementById('sessionTimeoutModal');
            if (modalElement) {
                // Remover el event listener para que no cierre la sesión
                modalElement.removeEventListener('hidden.bs.modal', this.handleModalHidden);
            }
            this.modal.hide();
        }
        // Reiniciar el temporizador de inactividad
        this.resetIdleTimer();
        // Mostrar notificación de éxito
        showNotification('Sesión extendida', 'Su sesión ha sido extendida exitosamente.', 'success');
    }
    resetIdleTimer() {
        // Destroy existing instance if present
        if (this.idleTimeoutInstance) {
            this.idleTimeoutInstance.destroy();
        }
        // Create new idle timeout instance
        this.idleTimeoutInstance = idleTimeout((element, timeout) => {
            this.handleIdleTimeout();
        }, {
            timeout: this.IDLE_TIME,
            loop: false,
            element: document.documentElement
        });
    }
    handleTimeoutExpired(path) {
        // Destruir el modal
        if (this.modal) {
            this.modal.dispose();
            this.modal = null;
        }
        // Eliminar el modal del DOM
        const modalElement = document.getElementById('sessionTimeoutModal');
        if (modalElement) {
            modalElement.remove();
        }
        // Cerrar sesión
        window.location.href = window.location.origin + '/' + path;
    }
}
export default SessionTimeoutManager;
